import { ReactComponent as InfoIcon } from 'icons/info.svg'
import { Avatar, Box, Card, Chip, Divider, FormControl, Paper, Select, TextField, Typography } from '@material-ui/core'
import styled from 'styled-components'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { JobFeedItemProps } from './types'
import { ArrowBack } from '@material-ui/icons'
import { useHexToRGB } from 'hooks/use-hex-to-rgb/useHexToRGB'
import { Button } from 'common/atoms/Button/Button'

interface StyledStatusChipProps {
  isActive: boolean
  theme?: any
  shouldUsePointer?: boolean
}

export const StyledContainer = styled(Box)`
  && {
    gap: 1.5rem;
    width: 100%;
    max-width: 77em;
    margin: 1.4375rem auto 0;
  }
`

export const StyledInnerContainer = styled(Box)`
  && {
    border-radius: 12px;
    overflow-y: auto;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`
export const StyledLeftContainer = styled(Box)`
  && {
    border-radius: 12px;
    flex: 1;
    background-color: ${({ theme }) => theme.colors.supplementary};
    align-self: flex-start;
  }
`

export const StyledLeftHeader = styled(Box)`
  && {
    gap: 1.125rem;
    padding: 1.333em;
    border-radius: 0.938rem 0.938rem 0 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.supplementary1};
  }
`

export const StyledLeftHeaderTitle = styled(Typography)`
  && {
    color: ${({ theme }) => theme.colors.accent1};
    margin-right: auto;
  }
`

export const StyledLeftHeaderArrow = styled(ArrowBack)`
  && {
    color: ${({ theme }) => theme.colors.accent1};
    margin-right: 0.75rem;
    cursor: pointer;
  }
`

export const StyledBackArrowSection = styled(Box)`
  && {
    display: flex;
  }
`

export const StyledJobCardWrapper = styled(Box)<{ isActive?: boolean }>`
  && {
    gap: 1.333em;
    grid-row-gap: 5px;
    padding: calc(1.3rem + 3px);
    cursor: pointer;
    border-radius: 12px;
    border: ${props => (props?.isActive ? `1px solid ${props.theme.colors.primary}` : `none`)};
    width: 100%;
  }
`

export const StyledWebImageWrapper = styled(Box)`
  && {
    width: 60px;
    height: 60px;
    border-radius: 100px;
    overflow: hidden;
    margin: 0 auto 0.875rem auto;
  }
`

export const StyledRightCard = styled(Box)`
  && {
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    border-radius: 15px 15px 15px 15px;
    padding: 32px 0;
    padding-top: 0;
  }
`

export const StyledSkillsContainer = styled(Box)`
  && {
    max-width: 100%;
    padding: 27px 2rem 39px 2rem;
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    color: ${({ theme }) => theme.colors.accent1};

    border-top: 2px solid ${({ theme }) => theme.colors.supplementary};
    margin-top: 20px;
  }
`

export const StyledSalaryContainer = styled(Box)`
  && {
    max-width: 100%;
    border-radius: 0 0 15px 15px;
    padding: 27px 2em 39px 2em;
    border-top: 2px solid ${({ theme }) => theme.colors.supplementary};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    color: ${({ theme }) => theme.colors.accent1};
  }
`

export const StyledWebJobDescription = styled(Box)`
  && {
    color: ${({ theme }) => theme.colors.accent1};
  }
`

export const MobileContainer = styled(Box)`
  && {
    margin: 13px 18px 47px 18px;
    max-width: 100%;
    min-height: 100vh;
    background-color: ${({ theme }) => theme.colors.supplementary};
    padding: 28px 20px 84px 20px;
    border-radius: 15px;
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
  }
`

export const JobDetailsContainer = styled(Box)<JobFeedItemProps>`
  && {
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    border-radius: ${(props: JobFeedItemProps) => (props.isDevice ? '0.94rem' : '1.875rem')};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    padding-top: ${(props: JobFeedItemProps) => (props.isDevice ? '0.938rem' : '0.6875rem')};
  }

  && > * {
    padding: ${(props: JobFeedItemProps) => (props.isDevice ? `0 0.6875rem` : `0 3.25rem`)};
  }
`

export const StyledImageWrapper = styled(Box)`
  && {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 100px;
    overflow: hidden;
  }
`

export const StyledContentText = styled(Box)<{ isDevice?: boolean }>`
  && {
    margin-bottom: 5px;
    color: ${props => props.theme.colors.accent1};
  }
`

export const StyledLocationText = styled(Typography)`
  && {
    color: ${props => props.theme.colors.accent};
  }
`

export const StyledDateStampText = styled(Typography)`
  && {
    color: ${props => props.theme.colors.accent};
    font-weight: 400;
    font-size: 0.75rem;
    font-weight: lighter;
  }
`

export const StyledCardTitle = styled(Typography)<JobFeedItemProps>`
  && {
    font-weight: 700;
    font-size: ${(props: JobFeedItemProps) => (props.isDevice ? '16px' : '16px')};
    color: ${props => props.theme.colors.accent1};
    line-height: 30.63px;
  }
`

export const StyledJobTitle = styled(Typography)<{ isDevice?: boolean; component?: string }>`
  && {
    margin-bottom: ${({ isDevice }) => (isDevice ? '0.25rem' : '0.44rem')};
    display: inline;
  }
`

export const StyledJobSelect = styled(Box)`
  && svg {
    fill: ${props => props.theme.colors.accent};
  }

  && {
    padding: 0 1.4em;
    height: 4.3em;
    position: relative;
    border-radius: 0.34rem;
    border: 2px solid ${props => props.theme.colors.accent};
    user-select: none;
    display: flex;
    align-items: center;
    background-color: ${props => props.theme.colors.supplementary1};
    line-height: 1.1876em;
    cursor: pointer;
  }
`

export const StyledNoJob = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.accent1};
  border-radius: 0 0 0.938rem 0.938rem;
  margin: 0;
  padding: 20px 0;
`

export const StyledDropDownList = styled(Paper)<{ offset?: string }>`
  position: absolute;
  transform: translateY(${props => props.offset ?? '0.7rem'});
  padding: 0.25rem 0.1rem;
  color: ${props => props.theme.colors.accent1} !important;
  width: 100%;
  left: 0;
  top: 100%;
  background-color: ${props => props.theme.colors.supplementary1};
  max-height: 6.7rem;
  overflow: auto;
  z-index: 1000;
`

export const StyledJobSelectValue = styled(Box)<{ isDevice: boolean }>`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${props => props.theme.colors.accent1} !important;
    font-size: ${props => (props.isDevice ? '0.97rem' : '1.0rem')};
    width: 100%;
  }
`

export const StyledRightWebCardTitle = styled(Typography)`
  && {
    color: ${props => props.theme.colors.accent1};
  }
`

export const StyledPillWrapper = styled(Box)<{ alignItems?: string }>`
  && {
    display: flex;
    gap: 1.5em;
    align-items: ${props => props.alignItems || 'center'};
  }
`

export const StyledImage = styled.img`
  height: 60px;
  width: 60px;
  object-fit: cover;
`

export const StyledStatusChip = styled(Chip)<StyledStatusChipProps>`
  && {
    color: ${(props: StyledStatusChipProps) =>
      props.isActive ? props.theme.colors.success : props.theme.colors.error};
    border: 1px solid
      ${(props: StyledStatusChipProps) => (props.isActive ? props.theme.colors.success : props.theme.colors.error)};
    background-color: ${props => props.theme.colors.supplementary};
    padding: 0.15em 0;
    margin: 0.25em;
    height: unset;
    cursor: ${props => (props?.shouldUsePointer ? 'pointer' : 'normal')};
    border-radius: 8px;
  }
`

//@ts-ignore
export const StyledHiredChip = styled(StyledStatusChip)`
  && {
    border: 1px solid
      ${(props: StyledStatusChipProps) => (props.isActive ? props.theme.colors.primary : props.theme.colors.error)};
    color: ${(props: StyledStatusChipProps) =>
      props.isActive ? props.theme.colors.primary : props.theme.colors.error};
  }
`

export const StyledJobDetailsCard = styled(Box)`
  && {
    display: flex;
    gap: 1em;
    margin-bottom: 1.5em;
    justify-content: space-between;
  }
`
export const StyledPostedJobDetailsSection = styled(Box)<{ isDevice?: boolean }>`
  && {
  }
`

export const StyledJobPillsWrapper = styled(Box)<{ isDevice?: boolean }>`
  && {
    display: flex;
    gap: 0.438rem;
    margin: ${({ isDevice }) => (isDevice ? '0.813rem 0 1.375rem 0' : ' 1rem 0 1.81rem 0')} ;
    text-align: ${({ isDevice }) => (isDevice ? 'center' : '')};
    flex-wrap: wrap;
  }
`

export const StyledPillContainer = styled(Box)`
  & {
    width: max-content;
  }
`

export const StyledAvatorAndTitleWrapper = styled(Box)<{ isDevice?: boolean }>`
  && {
    display: grid;
    grid-template-columns: ${({ isDevice }) => (isDevice ? '60px 13rem' : '60px auto')};
    gap: ${({ isDevice }) => (isDevice ? '0.813rem' : '2rem')};
    margin-left: ${({ isDevice }) => (isDevice ? '0.344rem' : '0')}
  }
`

export const StyledWebJobdetailsCard = styled(Box)`
  && {
    gap: 1em;
    margin-bottom: 0.7em;
    justify-content: space-between;
    padding: 0px 32px 0px 32px;
  }
`

export const StyledJobDescriptionTitle = styled(Typography)`
  && {
    font-weight: 700;
    font-size: 1rem;
    padding: 0.5rem 0;
  }
`

export const StyledJobDescriptionSubtitle = styled(Typography)`
  && {
    font-weight: 700;
    font-size: 1rem;
    color: ${props => props.theme.colors.accent1};
  }
`

export const StyledJobDescriptionText = styled(Typography)`
  && {
    font-weight: 400;
    margin-bottom: 1.625em;
    line-height: 1.5em;
    font-size: 0.875rem;
    word-break: break-all;
  }
`

export const SkillsWrapper = styled(Box)`
  && {
    margin: 2px 0px 2px 0px;
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    padding-top: 18px;
    padding-bottom: 18px;
  }
`

export const SalaryWrapper = styled(Box)`
  && {
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding-top: 18px;
    padding-bottom: 18px;
  }
`
export const SalaryDetailsWrapper = styled(Box)`
  && {
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
  }
`
export const StyledCard = styled(Box)`
  && {
    display: flex;
    gap: 0.563rem;
    background-color: ${props => props.theme.colors.bgPrimary};
    border-radius: 0.383rem;
    padding: 23px 17px 17px 17px;
    margin-bottom: 0.8em;
  }
`

export const StyledJobsSpan = styled.span`
  && {
    color: ${props => props.theme.colors.accent1};
    display: inline-block;
    width: 100%;
    font-size: 0.875rem;
    padding: 0;
    margin: 0;
    margin-left: 10px;
  }
`

export const StyledFormControl = styled(FormControl)`
  && {
    width: 100%;
  }
`

export const StyledSelect = styled(Select)`
  && {
    border: none;
    background-color: ${({ theme }) => theme.colors.bgPrimary};
    color: ${({ theme }) => theme.colors.primary};
    border-radius: 30px;
  }
`

export const StyledExpandMoreIcon = styled(ExpandMoreIcon)`
  && {
    cursor: pointer;
    padding: 10px;
    box-sizing: content-box;
  }
`
export const StyledSubmitButtonBox = styled(Box)`
  width: 100%;
  display: flex !important;
  justify-content: center !important;
  gap: 2rem;
  margin-bottom: 1rem;
`

export const StyledFeedImageWrapper = styled(Box)<{ isDevice: boolean }>`
  && {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }
`

export const StyledFeedImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`

export const StyledFeedJobCard = styled(Box)<{ background?: string }>`
  && {
    background-color: ${props => props.theme.colors[props.background ?? 'background']};
    border-radius: 1.07em;
    margin-top: 1.3em;
    padding: 1.33rem;
  }
`

export const StyledSeeAllText = styled(Box)`
  && {
    color: ${props => props.theme.colors.secondary};
    text-decoration: underline;
    font-size: 1.066em;
    cursor: pointer;
    margin: 0;
  }
`

export const StyledFeedJobDescription = styled(Typography)`
  && {
    font-size: 0.875rem;
    font-weight: 400;
    color: ${props => props.theme.colors.accent1};
    overflow: hidden;
    line-height: 1.3;
    max-height: calc(1.3 * 3);
    -webkit-box-orient: vertical;
    display: block;
    display: -webkit-box;
    overflow: hidden !important;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    margin-bottom: 0.75em;
  }
`

export const StyledFeedJobTitle = styled.span`
  && {
    color: ${props => props.theme.colors.accent1};
    font-weight: 700;
    font-size: 1rem;
    line-height: 1.3;
    padding-top: 0;
    margin-top: 0;
  }
`

export const StyledFeedJobSubtitle = styled(Box)`
  && {
    color: ${props => props.theme.colors.accent1};
    font-size: 0.875rem;
    font-weight: 300;
  }
`
export const StyledFeedLocationText = styled(Box)<{ isDevice: boolean }>`
  && {
    color: ${props => props.theme.colors.accent1};
    font-size: ${props => (props.isDevice ? '0.875rem' : '0.875rem')};
    font-weight: 300;
  }
`
export const StyledCreatedAtText = styled(Typography)`
  && {
    color: ${props => props.theme.colors.accent};
    font-size: 0.75rem;
  }
`

export const StyledButtonWrapper = styled(Box)`
  && {
    display: flex;
    gap: 1.313rem;
  }
`

export const SyledContentFeedButton = styled(Button)`
  && {
    padding: 0.313em 0.75em;
    border-radius: 6.25rem;
    min-width: 7.938rem;
  }
`

export const StyledWebCardTitle = styled(Typography)`
  && {
    color: ${props => props.theme.colors.accent1};
  }
`
export const StyledSubtitle = styled(Typography)`
  && {
    font-size: 1rem;
    font-weight: 500;
    color: ${props => props.theme.colors.accent1};
  }
`

export const StyledJobDetailsHeader = styled(Box)`
  && {
    margin: 13px 18px 13px 0px;
  }
`

export const StyledDescriptionButton = styled(Typography)`
  && {
    color: ${props => props?.theme?.colors?.primary};
    padding: 0;
    margin: 5px;
    border-color: ${props => props?.theme?.colors?.primary};
    font-weight: bold;
    cursor: pointer;
  }
`

export const StyledTypography = styled(Typography)`
  && {
    font-size: 0.875rem;
    font-weight: lighter;
  }
`

export const StyledSkillsText = styled(Typography)`
  && {
    font-size: 0.875rem;
  }
`

export const StyledWebJobCardText = styled(Typography)`
  && {
    font-size: 0.875rem;
  }
`

export const StyledSalaryText = styled(Typography)`
  && {
    font-size: 0.875rem;
  }
`

export const StyledButtonChip = styled(Chip)`
  && {
    font-weight: 700;
    padding: 0.1em 0.5em;
    border: 1px solid;
    background-color: ${({ disabled, theme }) =>
      disabled ? theme.colors.supplementary : theme.colors.primary}!important;
    color: ${({ disabled, theme }) => (disabled ? theme.colors.accent1 : theme.colors.bgPrimary)} !important;
    margin: 0.25em;
    cursor: pointer;
  }

  && * {
    font-size: 0.75rem;
  }
  :hover {
    background-color: ${props => props.theme.colors.primary} !important;
  }
  :focus {
    background-color: ${props => props.theme.colors.primary} !important;
  }
`

export const StyledCustomeMenuWrapper = styled(Box)`
  && {
    margin-bottom: 1em;
    margin-left: auto;
  }
`
export const JobFeedItemHeaderContainer = styled(Box)<{ isDevice: boolean }>`
  display: flex;
  flex-direction: ${(props: JobFeedItemProps) => (props.isDevice ? 'column' : '')};
  justify-content: center;
  align-items: start;
  width: 100%;
  margin-bottom: 0.5rem;
`
export const JobItemButton = styled(Button)<{ isDevice: boolean }>`
  border-radius: 50px !important;
  background-color: ${(props: JobFeedItemProps) =>
    props?.disabled ? props.theme.colors.supplementary : props.theme.colors.primary}!important;
  margin-left: 20px !important;
  color: ${(props: JobFeedItemProps) =>
    props?.disabled ? props.theme.colors.accent1 : props.theme.colors.bgPrimary} !important;
  font-weight: 900 !important;
  font-size: 12px !important;
  height: unset !important;
  padding: 5px 8px !important;
`

export const JobDetailsApplyBox = styled(Box)<JobFeedItemProps>`
  && {
    max-width: ${props => (props.isDevice ? '100%' : '17.809rem')};
    display: flex;
    flex-direction: row;
    justify-content: ${(props: JobFeedItemProps) => (props.isDevice && props.showAlertBox ? 'end' : 'center')};
    justify-content: ${(props: JobFeedItemProps) => !props.showAlertBox && 'start'};
  }
`
export const JobHeaderSubContainer = styled(Box)<{ isDevice: boolean }>`
  display: grid;
  grid-template-columns: ${props => (props.isDevice ? '0.5fr 2fr' : '0.5fr 2fr')};
`
export const JobItemFooterContainer = styled(Box)<{ isDevice: boolean }>`
  display: flex;
  flex-direction: ${props => (props.isDevice ? 'column' : 'row')};
  justify-content: ${props => (props.isDevice ? 'space-between' : 'space-between')};
  align-items: ${props => (props.isDevice ? 'center' : '')};
  width: 100%;
`
export const JobFeedItemHeaderTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-wrap: break-word;
  width: 247px;
  margin: 0 5em 0 1.933em;
`

export const StyledJobFeedItemBodyPill = styled(Box)`
  display: flex;
  padding: 0.5333em 1.733em 0.5333em 1.667em;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  background: ${(props: JobFeedItemProps) => props.theme.colors.supplementary1};
`

export const JobFeedItemTitle = styled.p`
  color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Montserrat;
  font-size: 1.23em;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.15px;
  margin: 0;
  margin-bottom: 5px;
  cursor: pointer;
`

export const JobFeedItemCompanyName = styled(Typography)`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: Montserrat;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    letter-spacing: 0.15px;
    margin: 0;
    margin-bottom: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: break-word;
  }
`
export const JobFeedItemWorkPlaceText = styled(Typography)<JobFeedItemProps>`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-size: 14px;
    font-weight: 300;
    margin: 0;
    margin-bottom: 5px;
    overflow: hidden;
    overflow-wrap: break-word;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 6;
    line-clamp: 6;
    -webkit-box-orient: vertical;
  }
`

export const JobDetailsWorkPlaceText = styled(Typography)<JobFeedItemProps>`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-size: ${(props: JobFeedItemProps) => (props.isDevice ? '14px' : '14px')};
    font-weight: 300;
    margin: 0;
    margin-bottom: 5px;
    overflow: hidden;
    overflow-wrap: break-word;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
`

export const StyledJobDetailsText = styled(Typography)<{ isDevice?: boolean }>`
  && {
    font-style: normal;
    font-weight: ${({ isDevice }) => (isDevice ? '300' : '700')};
    letter-spacing: 0.00938rem;
    line-height: 150%;
    font-weight: 300;
  }
`
export const JobFeedItemJobDateTagText = styled(Typography)<JobFeedItemProps>`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-weight: 300;
    margin: 0;
    margin-bottom: 1rem;
    font-feature-settings: 'clig' off, 'liga' off;
    font-style: normal;
    line-height: 150%;
    letter-spacing: 0.15px;
  }
`
export const JobDetailsJobDateTagText = styled.p<JobFeedItemProps>`
  color: ${(props: JobFeedItemProps) => props.theme.colors.accent};
  font-size: ${(props: JobFeedItemProps) => (props.isDevice ? '12px' : '12px')};
  font-weight: 300;
  margin: 0;
  margin-bottom: 5px;
`

export const StyledPostedAgoText = styled(Typography)<{ isDevice?: boolean }>`
  && {
    font-style: normal;
    font-weight: 300;
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    text-align: ${({ isDevice }) => (isDevice ? 'center' : '')};
    margin-bottom: ${({ isDevice }) => (isDevice ? '1.44rem' : '')};
  }
`
export const JobFeedItemBodyHeadingText = styled.p`
  color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
  font-size: 16px;
  font-weight: 900;
  margin: 0;
  margin-bottom: 8px;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  line-clamp: 6;
  -webkit-box-orient: vertical;
`
export const JobFeedItemBodyText = styled.p<{ isDevice: boolean }>`
  color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
  font-size: 14px;
  font-weight: 400;

  margin: 0;
  margin-bottom: 5px;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${props => (props.isDevice ? 6 : 3)};
  line-clamp: ${props => (props.isDevice ? 6 : 3)};
  -webkit-box-orient: vertical;
  word-break: break-all;
`
export const JobDetailsItemBodyText = styled(Typography)<JobFeedItemProps>`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-size: ${(props: JobFeedItemProps) => (props.isDevice ? '14px' : '14px')};
    font-weight: 400;
    font-style: normal;
    margin: 0;
    overflow: hidden;
    overflow-wrap: break-word;
    text-overflow: ellipsis;
    display: -webkit-box;
    height: auto;
  }
`
export const JobElipsisContainer = styled(Box)<{ isDevice: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: end;
  margin-bottom: ${(props: JobFeedItemProps) => (props.isDevice ? '' : '30px')};
  cursor: pointer;
  flex-shrink: 0;
  padding-right: ${(props: JobFeedItemProps) => (props.isDevice ? '' : '1em')};
`

export const CompanyProfileImg = styled(Avatar)<{ isDevice?: boolean }>`
  && {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
    cursor: pointer;
  }
`

export const StyledJobCompanyImg = styled(Avatar)<{ isDevice?: boolean }>`
  && {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
    cursor: pointer;
  }
`
export const JobFeedItemCard = styled(Box)<JobFeedItemProps>`
  border-radius: 10px;
  background-color: ${(props: JobFeedItemProps) =>
    props.invertedColor ? props.theme.colors.bgPrimary : props.theme.colors.supplementary};
  width: 100%;
  margin-top: 15px;
  margin-bottom: 15px;
`

export const JobFeedItemContainer = styled(Box)`
  width: 100%;
  margin-top: 10px;
  padding: 15px;
`
export const JobFeedCardDummy = styled(Box)`
  height: 150px;
  width: 100%;
  background-color: gray;
  border-radius: 20px;
`

export const JobShowAllText = styled(Typography)`
  && {
    text-align: right;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.accent};
  }
`

export const ApplyButtonContainer = styled(Box)<JobFeedItemProps>`
  && {
    margin: 0px 0px 2px 0px;
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    padding-top: 2.81rem;
    padding-bottom: 2.81rem;
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: row;
    justify-content: ${(props: JobFeedItemProps) => (props.isDevice ? 'start' : 'center')};
  }
`

export const AlertApplyBox = styled(Box)`
  background-color: ${({ theme }) => theme.colors.supplementary};
`

export const ContactDetailsWrapper = styled(Box)`
  && {
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.supplementary};
    border-radius: ${(props: JobFeedItemProps) => (props.isDevice ? '0 0 15px 15px' : ' 0 0 5px 5px')};
    box-shadow: rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
    margin-top: 1.8125rem;
    padding-top: 1.8125rem;
  }
`

export const RecruiterProfileImg = styled(Avatar)<{ isDevice?: boolean }>`
  && {
    width: ${({ isDevice }) => (isDevice ? '5rem' : '60px')};
    height: ${({ isDevice }) => (isDevice ? '5rem' : '60px')};
    flex-shrink: 0;
  }
`

export const RecruiterTitleWorkPlaceText = styled(Typography)<JobFeedItemProps>`
  && {
    color: ${(props: JobFeedItemProps) => props.theme.colors.accent1};
    font-weight: 700;
    text-align: center;
    line-height: 150%;
    margin: 0;
    overflow: hidden;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    font-style: normal;
    letter-spacing: 0.00938rem;
  }
`

export const StyledCardSubTitle = styled(Typography)<JobFeedItemProps>`
  && {
    font-weight: 700;
    font-size: ${(props: JobFeedItemProps) => (props.isDevice ? '16px' : '18px')};
    color: ${props => props.theme.colors.accent1};
    line-height: 30.63px;
    margin-bottom: 18px;
  }
`

//@ts-ignore
export const StyledRecruiterHeading = styled(StyledCardSubTitle)<{ isDevice?: boolean }>`
  && {
    text-align: center;
    font-style: normal;
    font-weight: 700;
    margin-bottom: ${({ isDevice }) => (isDevice ? '1.44rem' : '1.38rem')};
    line-height: 160%;
    letter-spacing: 0.00938rem;
  }
`
export const StyledRecruiterDetailsCard = styled(Box)<{ isDevice?: boolean }>`
  && {
    display: flex;
    flex-direction: column;
    gap: ${({ isDevice }) => (isDevice ? '1.44rem' : '1.38rem')};
    align-items: center;
  }
`

export const StyledRecruiterTitle = styled(Typography)<JobFeedItemProps>`
  && {
    font-weight: 700;
    color: ${props => props.theme.colors.accent1};
    line-height: 150%;
    overflow-wrap: anywhere;
    width: 100%;
    margin-bottom: ${(props: JobFeedItemProps) => (props.isDevice ? '0.81rem' : '1.38rem')};
    text-align: center;
    font-style: normal;
    letter-spacing: 0.00938rem;
  }
`

export const StyledInfiniteScrollContainer = styled(Box)`
  && > div {
    width: 100%;
  }
`

export const StyledInfoIcon = styled(InfoIcon)`
  && {
    margin-right: 0.125rem;
  }

  && * {
  }
`

export const StyledNotifyTextField = styled(TextField)`
  .MuiInput-input {
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
    overflow-y: scroll;
  }
  .MuiInput-root {
    border: none;
  }
  .MuiInput-underline {
    ::before {
      border-bottom: none !important;
      background-color: transparent;
    }
    ::after {
      border-bottom: none !important;
      background-color: transparent;
    }
    ::hover {
      border-bottom: none !important;
      background-color: transparent;
    }
  }
`

export const StyledNotifyButton = styled(Button)`
  && {
    width: 70%;
    border-radius: 70px;
  }
`

export const StyledJobMetrics = styled(Box)<{ isDevice: boolean }>`
  margin: 0 auto;
  border-radius: 12px;
  padding: 26px 33px;
  display: flex;
  align-items: center;
  gap: ${props => (props.isDevice ? '18px' : '60px')};
`

export const StyledMetric = styled(Box)`
  display: flex;
  flex-direction: column-reverse;
  gap: 3px;
`

export const StyledMetricLabel = styled(Typography)`
  border-radius: 100px;
  border: 1px solid ${props => props.theme.colors.secondary};
  text-align: center;
  padding: 3px 15px;
  color: ${props => props.theme.colors.accent1};
  font-weight: bold !important;
  font-size: 14px !important;
`

export const StyledMetricTitle = styled(Typography)<{ isDevice?: boolean }>`
  color: ${props => useHexToRGB(props.theme.colors.accent1, 0.7)};
  font-weight: bold !important;
  text-align: center;
  font-size: 13px !important;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isDevice ? 'flex-start' : 'center')};
`

export const StyledApplicantsCount = styled(Typography)`
  color: ${props => props.theme.colors.primary};
  font-weight: bold !important;
  font-size: 22px !important;
`

export const StyledDividerBox = styled(Box)`
  height: 50px;
  background-color: transparent;
  border: 1px solid ${props => props.theme.colors.bgPrimary};
  opacity: 0.4;
`
export const StyledDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.colors.supplementary1};
    height: 0.063rem;
    align-self: stretch;
  }
`

export const StyledJobCard = styled(Card)`
  && {
    box-shadow: none;
    border: 1px solid ${props => props.theme.colors.supplementary1};
    padding: 1.25rem 0.75rem;
  }
`

export const StyledHeaderBox = styled(Box)`
  && {
    width: 15rem;
    height: 2.375rem;
    display: flex;
    align-items: center;
    grid-gap: 0.75rem;
    margin-bottom: 0.875rem;
  }
`

export const StyledJobPillBox = styled(Box)`
  && {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.65rem;
  }
`

export const FeaturedJobsCompanyProfileImg = styled(Avatar)<{ isDevice?: boolean }>`
  && {
    width: 2.125rem;
    height: 2.125rem;
    flex-shrink: 0;
    cursor: pointer;
  }
`
